import React, { useState } from "react";
import { CountInput } from "@design-system/CountInput/CountInput";
import { createCustomInputsBySize } from "@design-system/Input/inputCustomInputs";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { VStack } from "@design-system/VStack/VStack";
import { BooleanControl, NumberControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import demoStyles from "./NumberInputsPlayground.module.scss";

type NumberLocale = "en-US" | "de-DE";

const inputsBySize = createCustomInputsBySize();

const toNonNegativeNumber = (value: number) =>
    Number.isFinite(value) ? Math.max(0, value) : undefined;

export const NumberInputsPlayground = () => {
    const [locale, setLocale] = useState<NumberLocale>("en-US");
    const [numberValue, setNumberValue] = useState<number | undefined>(
        12345.67,
    );
    const [monetaryValue, setMonetaryValue] = useState<number | undefined>(
        2499.5,
    );
    const [countValue, setCountValue] = useState(3);
    const [minimum, setMinimum] = useState(1);
    const [isDisabled, setIsDisabled] = useState(false);
    const [canBeNegative, setCanBeNegative] = useState(true);

    const thousandSeparator = locale === "en-US" ? "," : ".";
    const decimalSeparator = locale === "en-US" ? "." : ",";

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>NumberFormat locale</h2>
                        <ToggleGroup.Root<NumberLocale>
                            value={locale}
                            onValueChange={(value) => {
                                if (value) {
                                    setLocale(value);
                                }
                            }}
                        >
                            <ToggleGroup.Item value="en-US">
                                en-US
                            </ToggleGroup.Item>
                            <ToggleGroup.Item value="de-DE">
                                de-DE
                            </ToggleGroup.Item>
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>CountInput</h2>
                        <NumberControl
                            label="Minimum"
                            min={0}
                            value={minimum}
                            onChange={(value) => {
                                const nextMinimum = toNonNegativeNumber(value);
                                if (nextMinimum === undefined) {
                                    return;
                                }
                                setMinimum(nextMinimum);
                                setCountValue((currentValue) =>
                                    Math.max(currentValue, nextMinimum),
                                );
                            }}
                        />
                    </section>
                    <section>
                        <h2>All inputs</h2>
                        <BooleanControl
                            label="Disabled"
                            checked={isDisabled}
                            onChange={setIsDisabled}
                        />
                    </section>
                    <section>
                        <h2>NumberFormat and MonetaryInput</h2>
                        <BooleanControl
                            label="Allow negative values"
                            checked={canBeNegative}
                            onChange={setCanBeNegative}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary="Number · money · count"
                output={JSON.stringify(
                    {
                        NumberFormat: {
                            value: numberValue,
                            locale,
                            allowNegative: canBeNegative,
                        },
                        MonetaryInput: {
                            value: monetaryValue,
                            allowNegative: canBeNegative,
                        },
                        CountInput: {
                            value: countValue,
                            min: minimum,
                        },
                        disabled: isDisabled,
                    },
                    null,
                    2,
                )}
            >
                <VStack gap={8} align="start">
                    <code className={demoStyles.fieldLabel}>NumberFormat</code>
                    <div className={demoStyles.numberField}>
                        <NumberFormat
                            value={numberValue ?? ""}
                            onValueChange={(values) =>
                                setNumberValue(values.floatValue)
                            }
                            thousandSeparator={thousandSeparator}
                            decimalSeparator={decimalSeparator}
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={canBeNegative}
                            disabled={isDisabled}
                            customInput={inputsBySize.xl}
                        />
                    </div>
                </VStack>
                <VStack gap={8} align="start">
                    <code className={demoStyles.fieldLabel}>MonetaryInput</code>
                    <div className={demoStyles.numberField}>
                        <MonetaryInput
                            value={monetaryValue}
                            onValueChange={setMonetaryValue}
                            allowNegative={canBeNegative}
                            disabled={isDisabled}
                            showCurrencySymbolIfEmpty
                            size="sm"
                        />
                    </div>
                </VStack>
                <VStack gap={8} align="start">
                    <code className={demoStyles.fieldLabel}>CountInput</code>
                    <div className={demoStyles.numberField}>
                        <CountInput
                            value={countValue}
                            onValueChange={setCountValue}
                            min={minimum}
                            disabled={isDisabled}
                        />
                    </div>
                </VStack>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
